<template>
  <div class="grid">
    <!-- Nombre -->
    <div class="col-12 md:col-6 flex flex-column">
      <InputText v-model="nombre" placeholder="Nombre" class="w-full" />
      <small v-if="nombreError" class="p-error">{{ nombreError }}</small>
    </div>

    <!-- Título -->
    <div class="col-12 md:col-6 flex flex-column">
      <InputText v-model="titulo" placeholder="Título" class="w-full" />
      <small v-if="tituloError" class="p-error">{{ tituloError }}</small>
    </div>

    <!-- Tipo -->
    <div class="col-12">
      <Dropdown
        v-model="tipo"
        placeholder="Tipo"
        :options="tipos"
        optionLabel="name"
        optionValue="name"
        class="w-full"
      />
      <small v-if="tipoError" class="p-error">{{ tipoError }}</small>

      <div v-if="showPieList" class="mt-2">
        <Button label="Añadir valor" @click="addPieListElement()" />
      </div>
    </div>

    <!-- Añadir listado para el tipo radar -->
    <template v-if="showRadarList">
      <div class="col-12 md:col-6">
        <Button
          label="Añadir conjunto de datos"
          @click="addRadarDatasetElement()"
          class="w-full"
        />
      </div>
      <div class="col-12 md:col-6">
        <Button
          label="Añadir parámetro"
          @click="addRadarParamElement()"
          class="w-full"
        />
      </div>
    </template>

    <!-- Lista de valores del gráfico tipo tarta, sankey o sankey circular -->
    <div v-if="showPieList" class="col-12">
      <div class="values-list-container">
        <div
          v-for="(n, index) in totalPieList"
          :key="n"
          class="grid"
          :class="{ 'mt-1': index > 0 }"
        >
          <div class="col-12 flex align-items-center gap-3">
            <InputText
              v-model="names[n - 1]"
              placeholder="Valor"
              class="w-full"
            />
            <ColorPicker v-model="colors[n - 1]" />
            <Button
              icon="pi pi-trash"
              @click="deletePieRegister(n)"
              class="delete-button"
              severity="danger"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Eje X y Eje Y -->
    <template v-if="showLabelsInputs">
      <div class="col-12 md:col-6">
        <InputText
          v-model="xAxis"
          placeholder="Nombre del eje X"
          class="w-full"
        />
        <small v-if="xAxisError" class="p-error">{{ xAxisError }}</small>
      </div>
      <div class="col-12 md:col-6">
        <InputText
          v-model="yAxis"
          placeholder="Nombre del eje Y"
          class="w-full"
        />
        <small v-if="yAxisError" class="p-error">{{ yAxisError }}</small>
      </div>
      <div class="col-12 flex align-items-center gap-2">
        <label class="font-bold">Color de las barras</label>
        <ColorPicker v-model="color" />
      </div>
    </template>

    <!-- Si el gráfico es tipo radar -->
    <template v-if="showRadarList">
      <!-- Lista de conjunto de datos -->
      <div class="col-12 md:col-6">
        <div class="values-list-container">
          <div
            v-for="(n, index) in totalRadarDatasets"
            :key="n"
            class="grid"
            :class="{ 'mt-1': index > 0 }"
          >
            <div class="col-12 flex align-items-center gap-3">
              <InputText
                v-model="datasets[n - 1]"
                placeholder="Conjunto de datos"
                class="w-full"
              />
              <ColorPicker v-model="datasetsColors[n - 1]" />
              <Button
                icon="pi pi-trash"
                @click="deleteRadarDatasetRegister(n)"
                class="delete-button"
                severity="danger"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Lista de valores -->
      <div class="col-12 md:col-6">
        <div class="values-list-container">
          <div
            v-for="(n, index) in totalRadarList"
            :key="n"
            class="grid"
            :class="{ 'mt-1': index > 0 }"
          >
            <div class="col-12 flex align-items-center gap-3">
              <InputText
                v-model="params[n - 1]"
                placeholder="Parámetro"
                class="w-full"
              />
              <Button
                icon="pi pi-trash"
                @click="deleteRadarParamRegister(n)"
                class="delete-button"
                severity="danger"
              />
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Selector de ubicación del gráfico -->
    <div class="col-12">
      <SelectGraphLocation ref="selectGraphLocationRef" />
    </div>

    <!-- Botones de acción -->
    <div class="col-12 flex justify-content-between flex-wrap gap-2">
      <div class="flex flex-wrap gap-2">
        <Button label="Cancelar" @click="cancel()" severity="secondary" />
        <Button
          label="Comprobar áreas"
          @click="checkAreas(true)"
          severity="secondary"
        />
      </div>

      <Button :label="addLabel" @click="saveGraph()" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed, nextTick } from "vue";
import InputText from "primevue/inputtext";
import ColorPicker from "primevue/colorpicker";
import Dropdown from "primevue/dropdown";
import Button from "primevue/button";
import { useToast } from "primevue/usetoast";

import SelectGraphLocation from "@/pages/_root/reports-dialog/form-five/SelectGraphLocation.vue";

// #region Props, Emits
const emit = defineEmits(["save-graph", "cancel"]);

const props = defineProps({
  graphs: {
    type: Array,
    default: () => [],
  },
  editGraph: {
    type: Object,
    default: null,
  },
});
// #endregion

// #region Params, Ref, Reactives, Computed
const toast = useToast();

const selectGraphLocationRef = ref(null);

const nombre = ref(null);
const nombreError = ref(null);

const titulo = ref(null);
const tituloError = ref(null);

const tipo = ref(null);
const tipoError = ref(null);

// showPieList
const totalPieList = ref(1);
const names = ref([]);
const colors = ref(["005da3"]);

// showLabelsInputs
const color = ref(null);
const xAxis = ref(null);
const xAxisError = ref(null);
const yAxis = ref(null);
const yAxisError = ref(null);

// showRadarList
const totalRadarDatasets = ref(1);
const datasets = ref([]);
const datasetsColors = ref(["005da3"]);
const totalRadarList = ref(1);
const params = ref([]);

const tipos = [
  {
    name: "Tarta",
  },
  {
    name: "Barras horizontales",
  },
  {
    name: "Barras verticales",
  },
  {
    name: "Sankey",
  },
  {
    name: "Sankey circular",
  },
  {
    name: "Lineas",
  },
  {
    name: "Radar",
  },
];

const showPieList = computed(() => {
  return (
    tipo.value == "Tarta" ||
    tipo.value == "Sankey circular" ||
    tipo.value == "Sankey"
  );
});

const showLabelsInputs = computed(() => {
  return (
    tipo.value == "Barras horizontales" ||
    tipo.value == "Barras verticales" ||
    tipo.value == "Lineas"
  );
});

const showRadarList = computed(() => {
  return tipo.value == "Radar";
});

const addLabel = computed(() => {
  return props.editGraph ? "Actualizar" : "Añadir";
});
// #endregion

// #region Functions
onMounted(() => {
  if (props.editGraph) {
    const graph = JSON.parse(JSON.stringify(props.editGraph));

    nombre.value = graph.nombre;
    titulo.value = graph.titulo;
    tipo.value = graph.tipo;

    const propiedades = graph.propiedades;

    selectGraphLocationRef.value.loadAreas(propiedades.graphLocation);

    nextTick(() => {
      if (showLabelsInputs.value) {
        xAxis.value = propiedades.xAxis;
        yAxis.value = propiedades.yAxis;
        color.value = propiedades.color;
      }

      if (showPieList.value) {
        totalPieList.value = propiedades.nombres.length;

        nextTick(() => {
          names.value = propiedades.nombres;
          colors.value = propiedades.colores;
        });
      }

      if (showRadarList.value) {
        totalRadarDatasets.value = propiedades.datasets.length;
        totalRadarList.value = propiedades.params.length;

        nextTick(() => {
          datasets.value = propiedades.datasets;
          datasetsColors.value = propiedades.datasetsColores;
          params.value = propiedades.params;
        });
      }
    });
  }
});

function saveGraph() {
  if (validateForm()) {
    return;
  }

  let idGrafico = null;

  if (props.editGraph != null) {
    idGrafico = props.editGraph.idGrafico;
  } else if (props.graphs.length == 0) {
    idGrafico = 1;
  } else {
    idGrafico = props.graphs[props.graphs.length - 1].idGrafico + 1;
  }

  const graph = {
    idGrafico,
    nombre: nombre.value,
    titulo: titulo.value,
    tipo: tipo.value,
    propiedades: {
      graphLocation: selectGraphLocationRef.value?.devices,
      nombres: names.value,
      colores: colors.value,
      xAxis: xAxis.value,
      yAxis: yAxis.value,
      color: color.value,
      datasets: datasets.value,
      datasetsColores: datasetsColors.value,
      params: params.value,
    },
  };

  emit("save-graph", graph);
}

function checkTipoError() {
  if (!tipo.value) {
    tipoError.value = "El tipo es requerido";
  }

  if (showPieList.value) {
    if (totalPieList.value == 0) {
      tipoError.value = "Debe añadir al menos un valor";
    } else {
      if (names.value.length == 0) {
        tipoError.value = mustFillText;
      } else {
        names.value.forEach((name) => {
          if (!name || (typeof name === "string" && !name.trim())) {
            tipoError.value = mustFillText;
          }
        });
      }
    }
  }

  const mustFillText = "Debe rellenar todos los valores";

  if (showRadarList.value) {
    if (totalRadarDatasets.value == 0) {
      tipoError.value = "Debe añadir al menos un conjunto de datos";
    } else {
      if (
        datasets.value.length == 0 ||
        totalRadarDatasets.value != datasets.value.length
      ) {
        tipoError.value = mustFillText;
      } else {
        datasets.value.forEach((dataset) => {
          if (!dataset || (typeof dataset === "string" && !dataset.trim())) {
            tipoError.value = mustFillText;
          }
        });
      }
    }

    if (totalRadarList.value == 0) {
      tipoError.value = "Debe añadir al menos un parámetro";
    } else {
      if (
        params.value.length == 0 ||
        totalRadarList.value != params.value.length
      ) {
        tipoError.value = mustFillText;
      } else {
        params.value.forEach((param) => {
          if (!param || (typeof param === "string" && !param.trim())) {
            tipoError.value = mustFillText;
          }
        });
      }
    }
  }
}

function checkLabelsError() {
  if (showLabelsInputs.value) {
    if (!xAxis.value?.trim()) {
      xAxisError.value = "El nombre del eje X es requerido";
    }

    if (!yAxis.value?.trim()) {
      yAxisError.value = "El nombre del eje Y es requerido";
    }
  }
}

function checkAreas(showToast = false) {
  const valid = selectGraphLocationRef.value.validateAreas();

  if (showToast) {
    if (valid) {
      toast.add({
        severity: "success",
        summary: "Áreas correctas",
        detail: `Las áreas seleccionadas son correctas`,
        life: 3000,
      });
    } else {
      toast.add({
        severity: "error",
        summary: "Áreas incorrectas",
        detail: `Las áreas seleccionadas no son correctas`,
        life: 3000,
      });
    }
  }

  return valid;
}

function validateForm() {
  let error = false;

  nombreError.value = null;
  tituloError.value = null;
  tipoError.value = null;
  xAxisError.value = null;
  yAxisError.value = null;

  if (!nombre.value?.trim()) {
    nombreError.value = "El nombre es requerido";
    error = true;
  }

  if (!titulo.value?.trim()) {
    tituloError.value = "El título es requerido";
    error = true;
  }

  checkTipoError();
  checkLabelsError();

  if (tipoError.value || xAxisError.value || yAxisError.value) {
    error = true;
  }

  if (!checkAreas()) {
    error = true;
  }

  return error;
}

function addPieListElement() {
  totalPieList.value++;

  nextTick(() => {
    colors.value[totalPieList.value - 1] = `005da3`;
  });
}

function addRadarDatasetElement() {
  totalRadarDatasets.value++;

  nextTick(() => {
    datasetsColors.value[totalRadarDatasets.value - 1] = `005da3`;
  });
}

function addRadarParamElement() {
  totalRadarList.value++;
}

function deletePieRegister(index) {
  index--;

  names.value.splice(index, 1);
  colors.value.splice(index, 1);

  totalPieList.value--;
}

function deleteRadarDatasetRegister(index) {
  index--;

  datasets.value.splice(index, 1);
  datasetsColors.value.splice(index, 1);

  totalRadarDatasets.value--;
}

function deleteRadarParamRegister(index) {
  index--;

  params.value.splice(index, 1);

  totalRadarList.value--;
}

function resetValues() {
  nombre.value = null;
  titulo.value = null;
  tipo.value = null;
  totalPieList.value = 1;
  names.value = [];
  colors.value = ["005da3"];
  color.value = null;
  xAxis.value = null;
  yAxis.value = null;
  totalRadarDatasets.value = 1;
  datasets.value = [];
  datasetsColors.value = ["005da3"];
  totalRadarList.value = 1;
  params.value = [];
}

function cancel() {
  resetValues();
  emit("cancel");
}
// #endregion

// #region Effects, Hooks
// #endregion

// #region Provide, Expose
// #endregion
</script>

<style lang="scss" scoped>
.values-list-container {
  max-height: 20rem;
  overflow-y: scroll;
  overflow-x: hidden;
}

.delete-button {
  aspect-ratio: 1;
}
</style>
